/**
 * Session state: who is signed in, and which farm and batch they are working on.
 *
 * Client state only. Server data lives in TanStack Query (§5) — putting a batch
 * list in here would create a second copy that goes stale.
 *
 * Deliberately dependency-free: `useSyncExternalStore` is enough for three
 * fields, and §9 rule 12 asks for a reason before adding a state library.
 */
import { useSyncExternalStore } from 'react'

export interface SessionState {
  accessToken: string | null
  userId: string | null
  farmId: string | null
  activeBatchId: string | null
}

const EMPTY: SessionState = {
  accessToken: null,
  userId: null,
  farmId: null,
  activeBatchId: null,
}

let state: SessionState = EMPTY
const listeners = new Set<() => void>()

export function getSession(): SessionState {
  return state
}

export function setSession(patch: Partial<SessionState>): void {
  state = { ...state, ...patch }
  for (const listener of listeners) listener()
}

export function clearSession(): void {
  state = EMPTY
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useSession(): SessionState {
  return useSyncExternalStore(subscribe, getSession, getSession)
}
